/**
 * The GSTR-3B as an editable form, laid out as the portal has it.
 * @file src/web/features/admin/components/Gstr3bEditor.tsx
 *
 * Every figure is an input, prefilled from what oddits computed, and the
 * derived ones — 4C, the tax payable, the set-off in 6.1 — recalculate as the
 * inputs change (gstr3b-form.ts holds the arithmetic and the checks).
 *
 * This component sends nothing. Editing is local to the screen; the portal
 * steps below it (Upload, fetch the GSTN summary, offset, EVC, file) stay
 * separate actions the person presses, and filing signs GSTN's own summary.
 */
import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle2, Plus, RotateCcw, X } from 'lucide-react';
import { GST_STATES } from '../../../../api/lib/gst-states';
import {
  formFromPortal, withGstn51, netItc, payment, validate, errorsOf, buildUploadPayload,
  INTER_KINDS,
  type Gstr3bForm, type Heads, type Ledgers, type Row, type Tax4, type Issue, type InterKind, type ItcAvl,
} from './gstr3b-form';

const inr = (n: number) => (Number.isFinite(n) ? n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—');

/* ── one number box ────────────────────────────────────────────────────── */

/** A money input: free text while typing, two decimals once left. */
function Num({ value, onChange, bad, label, disabled }: {
  value: number; onChange: (v: number) => void; bad?: boolean; label: string; disabled?: boolean;
}) {
  const [draft, setDraft] = useState<string | null>(null);
  /* A change from outside — Reset, GSTN's 5.1 arriving — must not be hidden behind what was last typed. */
  useEffect(() => {
    if (draft === null) return;
    const d = draft.trim() === '' ? 0 : Number(draft);
    if (!(Object.is(d, value) || (Number.isNaN(d) && Number.isNaN(value)))) setDraft(null);
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps
  const shown = draft ?? (Number.isFinite(value) ? (value === 0 ? '' : String(value)) : 'NaN');
  if (disabled) return <span className="block px-2 py-1 text-right tabular-nums text-ink-300" aria-label={label}>—</span>;
  return (
    <input
      aria-label={label} inputMode="decimal" value={shown} placeholder="0.00"
      onFocus={() => setDraft(Number.isFinite(value) && value !== 0 ? String(value) : '')}
      onChange={(e) => { setDraft(e.target.value); const t = e.target.value.trim(); onChange(t === '' ? 0 : Number(t)); }}
      onBlur={() => { if (draft !== null && Number.isFinite(value)) onChange(Math.round(value * 100) / 100); setDraft(null); }}
      className={`w-28 rounded-lg border px-2 py-1 text-right tabular-nums text-ink-900 focus-visible:outline-none ${bad ? 'border-danger-300 bg-danger-50' : 'border-ink-200 bg-white focus:border-ink-400'}`}
    />
  );
}

const Calc = ({ v, strong }: { v: number; strong?: boolean }) => (
  <span className={`block px-2 py-1 text-right tabular-nums ${strong ? 'font-semibold text-ink-900' : 'text-ink-700'}`}>{inr(v)}</span>
);

function Card({ title, note, cols, children }: { title: string; note?: string; cols: string[]; children: React.ReactNode }) {
  return (
    <div className="overflow-hidden rounded-2xl border border-ink-200 bg-white">
      <div className="border-b border-ink-200 bg-ink-50 px-5 py-3">
        <p className="text-sm font-semibold text-ink-900">{title}</p>
        {note && <p className="mt-0.5 max-w-3xl text-xs text-ink-500">{note}</p>}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[42rem] text-sm">
          <thead>
            <tr className="border-b border-ink-100 text-left text-xs uppercase tracking-wide text-ink-400">
              {cols.map((c, i) => <th key={c} className={`px-4 py-2.5 font-medium ${i === 0 ? '' : 'text-right'}`}>{c}</th>)}
            </tr>
          </thead>
          <tbody>{children}</tbody>
        </table>
      </div>
    </div>
  );
}

const Sub = ({ label, span }: { label: string; span: number }) => (
  <tr className="border-y border-ink-100 bg-ink-50/70"><td colSpan={span} className="px-4 py-2 text-xs font-semibold uppercase tracking-wide text-ink-600">{label}</td></tr>
);

const Label = ({ children, muted }: { children: React.ReactNode; muted?: boolean }) => (
  <td className={`px-4 py-1.5 align-middle ${muted ? 'text-ink-500' : 'text-ink-800'}`}>{children}</td>
);

/* ── the editor ────────────────────────────────────────────────────────── */

export interface Gstr3bEditorProps {
  /** The `portal` block oddits computed for the period. */
  portal: any;
  gstin: string | null;
  period: string;
  /** 4A(5) as GSTR-2B supports it; null when no 2B is loaded. */
  twoB: Heads | null;
  /** GSTN's 5.1 once read (`{interest, lateFee}` as heads); null until fetched. */
  gstn51: { interest?: Heads | null; lateFee?: Heads | null } | null;
  /** Credit and cash ledgers once read from the portal; null until then. */
  ledgers: Ledgers | null;
}

export function Gstr3bEditor({ portal, gstin, period, twoB, gstn51, ledgers }: Gstr3bEditorProps) {
  const computed = useMemo(() => formFromPortal(portal), [portal]);
  const [form, setForm] = useState<Gstr3bForm>(computed);
  const [edited, setEdited] = useState(false);

  /* A new period or fresh books replace the form — unless the person has typed
     into it, in which case their figures are not thrown away by a refetch. */
  useEffect(() => { if (!edited) setForm(computed); }, [computed, edited]);
  useEffect(() => { setEdited(false); setForm(computed); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [period, gstin]);
  /* GSTN's own 5.1 fills the table when it arrives; it is a fetch the person asked for. */
  useEffect(() => { if (gstn51) setForm((f) => withGstn51(f, gstn51)); }, [gstn51]);

  const change = (fn: (f: Gstr3bForm) => void) => {
    setForm((f) => { const c = structuredClone(f); fn(c); return c; });
    setEdited(true);
  };
  const reset = () => { setEdited(false); setForm(gstn51 ? withGstn51(computed, gstn51) : computed); };

  const twoBTax4: Tax4 | null = twoB ? { iamt: twoB.igst, camt: twoB.cgst, samt: twoB.sgst, csamt: twoB.cess } : null;
  const issues = useMemo(() => validate(form, { twoB: twoBTax4 }), [form, twoBTax4?.iamt, twoBTax4?.camt, twoBTax4?.samt, twoBTax4?.csamt]); // eslint-disable-line react-hooks/exhaustive-deps
  const errs = errorsOf(issues);
  const bad = (path: string) => issues.some((i) => i.level === 'error' && (i.field === path || path.startsWith(`${i.field}.`)));
  const net = netItc(form);
  const pay = useMemo(() => (errs.length ? null : payment(form, ledgers)), [form, ledgers, errs.length]);
  const ready = useMemo(() => buildUploadPayload(form, { gstin: gstin ?? '', fp: period }, { twoB: twoBTax4 }).ok, [form, gstin, period, twoBTax4?.iamt]); // eslint-disable-line react-hooks/exhaustive-deps

  /* Row editors. `keys` lists which of the five columns take a figure. */
  const ALL: (keyof Row)[] = ['txval', 'iamt', 'camt', 'samt', 'csamt'];
  const row5 = (path: string, label: string, get: (f: Gstr3bForm) => Row, on: (keyof Row)[]) => (
    <>
      {ALL.map((k) => (
        <td key={k} className="px-2 py-1 text-right">
          <Num label={`${label} ${k}`} disabled={!on.includes(k)} value={get(form)[k]} bad={bad(`${path}.${k}`)}
            onChange={(v) => change((f) => { get(f)[k] = v; })} />
        </td>
      ))}
    </>
  );
  /** 3.1(c), 3.1(e), 3.1.1(ii): a taxable value and nothing else. */
  const valueOnly = (path: 'osup_nil_exmp' | 'osup_nongst' | 'eco_reg_sup', label: string) => (
    <>
      <td className="px-2 py-1 text-right"><Num label={`${label} taxable value`} value={form[path]} bad={bad(path)} onChange={(v) => change((f) => { f[path] = v; })} /></td>
      {[0, 1, 2, 3].map((i) => <td key={i} className="px-2 py-1"><Num label="" value={0} onChange={() => {}} disabled /></td>)}
    </>
  );

  const unavailable51 = !gstn51;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-ink-200 bg-white px-5 py-3">
        <p className="max-w-3xl text-xs text-ink-600">
          Every figure below can be changed; 4C and 6.1 recalculate. <span className="font-medium text-ink-800">Edits stay on this screen</span> —
          Upload still sends the figures oddits computed from the books, and the portal steps below are unchanged.
        </p>
        <button onClick={reset} disabled={!edited}
          className="inline-flex items-center gap-1.5 rounded-full border border-ink-300 bg-white px-3.5 py-1.5 text-xs font-semibold text-ink-700 hover:bg-ink-50 disabled:opacity-40">
          <RotateCcw className="h-3.5 w-3.5" /> Reset to computed
        </button>
      </div>

      {/* 3.1 */}
      <Card title="3.1 Details of Outward supplies and inward supplies liable to reverse charge"
        cols={['Nature of Supplies', 'Total taxable value', 'Integrated tax', 'Central tax', 'State/UT tax', 'Cess']}>
        <tr className="border-b border-ink-100"><Label>(a) Outward taxable supplies (other than zero rated, nil rated and exempted)</Label>{row5('osup_det', '3.1(a)', (f) => f.osup_det, ALL)}</tr>
        <tr className="border-b border-ink-100"><Label>(b) Outward taxable supplies (zero rated)</Label>{row5('osup_zero', '3.1(b)', (f) => f.osup_zero, ['txval', 'iamt', 'csamt'])}</tr>
        <tr className="border-b border-ink-100"><Label>(c) Other outward supplies (nil rated, exempted)</Label>{valueOnly('osup_nil_exmp', '3.1(c)')}</tr>
        <tr className="border-b border-ink-100"><Label>(d) Inward supplies (liable to reverse charge)</Label>{row5('isup_rev', '3.1(d)', (f) => f.isup_rev, ALL)}</tr>
        <tr><Label>(e) Non-GST outward supplies</Label>{valueOnly('osup_nongst', '3.1(e)')}</tr>
      </Card>

      {/* 3.1.1 */}
      <Card title="3.1.1 Details of Supplies notified under section 9(5) of the CGST Act, 2017"
        cols={['Nature of Supplies', 'Total taxable value', 'Integrated tax', 'Central tax', 'State/UT tax', 'Cess']}>
        <tr className="border-b border-ink-100"><Label>(i) Taxable supplies on which electronic commerce operator pays tax u/s 9(5)</Label>{row5('eco_sup', '3.1.1(i)', (f) => f.eco_sup, ALL)}</tr>
        <tr><Label>(ii) Taxable supplies made by a registered person through an electronic commerce operator</Label>{valueOnly('eco_reg_sup', '3.1.1(ii)')}</tr>
      </Card>

      {/* 3.2 */}
      <Card title="3.2 Of the supplies made in 3.1(a) and 3.1.1(i), details of inter-state supplies made"
        cols={['Place of supply', 'Total taxable value', 'Integrated tax', '']}>
        {INTER_KINDS.map((kind) => (
          <InterGroup key={kind} kind={kind} form={form} change={change} bad={bad} />
        ))}
      </Card>

      {/* 4 */}
      <Card title="4. Eligible ITC" note={twoB ? "4A(5) is checked against this period's GSTR-2B." : 'No 2B is loaded, so 4A(5) cannot be checked against it.'}
        cols={['Details', 'Integrated tax', 'Central tax', 'State/UT tax', 'Cess']}>
        <Sub label="A. ITC Available (whether in full or part)" span={5} />
        {([['IMPG', '(1) Import of goods'], ['IMPS', '(2) Import of services'], ['ISRC', '(3) Inward supplies liable to reverse charge (other than 1 & 2 above)'], ['ISD', '(4) Inward supplies from ISD'], ['OTH', '(5) All other ITC']] as [ItcAvl, string][]).map(([ty, label]) => (
          <tr key={ty} className="border-b border-ink-100"><Label>{label}</Label>{taxCells(`itc_avl.${ty}`, `4A ${ty}`, (f) => f.itc_avl[ty])}</tr>
        ))}
        <Sub label="B. ITC Reversed" span={5} />
        <tr className="border-b border-ink-100"><Label>(1) As per rules 38, 42 & 43 of CGST Rules and section 17(5)</Label>{taxCells('itc_rev.RUL', '4B(1)', (f) => f.itc_rev.RUL)}</tr>
        <tr className="border-b border-ink-100"><Label>(2) Others</Label>{taxCells('itc_rev.OTH', '4B(2)', (f) => f.itc_rev.OTH)}</tr>
        <tr className="border-b border-ink-100 bg-ink-50/70 font-semibold"><Label>C. Net ITC available (A − B) — calculated</Label>
          <td className="px-2 py-1"><Calc v={net.iamt} strong /></td><td className="px-2 py-1"><Calc v={net.camt} strong /></td>
          <td className="px-2 py-1"><Calc v={net.samt} strong /></td><td className="px-2 py-1"><Calc v={net.csamt} strong /></td>
        </tr>
        <Sub label="D. Other Details" span={5} />
        <tr className="border-b border-ink-100"><Label muted>(1) ITC reclaimed which was reversed under Table 4(B)(2) in earlier tax period</Label>{taxCells('itc_inelg.RUL', '4D(1)', (f) => f.itc_inelg.RUL)}</tr>
        <tr><Label muted>(2) Ineligible ITC under section 16(4) & ITC restricted due to PoS rules</Label>{taxCells('itc_inelg.OTH', '4D(2)', (f) => f.itc_inelg.OTH)}</tr>
      </Card>

      {/* 5 */}
      <Card title="5. Values of exempt, nil-rated and non-GST inward supplies" cols={['Nature of Supplies', 'Inter-State supplies', 'Intra-State supplies']}>
        {(['GST', 'NONGST'] as const).map((ty) => (
          <tr key={ty} className="border-b border-ink-100 last:border-0">
            <Label>{ty === 'GST' ? 'From a supplier under composition scheme, Exempt and Nil rated supply' : 'Non GST supply'}</Label>
            {(['inter', 'intra'] as const).map((k) => (
              <td key={k} className="px-2 py-1 text-right"><Num label={`5 ${ty} ${k}`} value={form.inward[ty][k]} bad={bad(`inward.${ty}.${k}`)} onChange={(v) => change((f) => { f.inward[ty][k] = v; })} /></td>
            ))}
          </tr>
        ))}
      </Card>

      {/* 5.1 */}
      <Card title="5.1 Interest and Late fee for previous tax period"
        note={unavailable51
          ? 'From portal, not fetched yet — GSTN computes interest (rule 88B) and late fee. Read them below, or type them if you know them.'
          : 'Filled from GSTN’s own figures. Edit only if you know better than the portal.'}
        cols={['Details', 'Integrated tax', 'Central tax', 'State/UT tax', 'Cess']}>
        <tr className="border-b border-ink-100"><Label>Interest</Label>{taxCells('interest', '5.1 interest', (f) => f.interest)}</tr>
        <tr>
          <Label>Late fee</Label>
          <td className="px-2 py-1"><Num label="" value={0} onChange={() => {}} disabled /></td>
          <td className="px-2 py-1 text-right"><Num label="5.1 late fee camt" value={form.lateFee.camt} bad={bad('lateFee.camt')} onChange={(v) => change((f) => { f.lateFee.camt = v; })} /></td>
          <td className="px-2 py-1 text-right"><Num label="5.1 late fee samt" value={form.lateFee.samt} bad={bad('lateFee.samt')} onChange={(v) => change((f) => { f.lateFee.samt = v; })} /></td>
          <td className="px-2 py-1"><Num label="" value={0} onChange={() => {}} disabled /></td>
        </tr>
      </Card>

      {/* 6.1 */}
      <PaymentCard pay={pay} blocked={errs.length > 0} />

      <Checks issues={issues} ready={ready} />
    </div>
  );

  /** The four tax-head inputs of a table-4 or 5.1 row. */
  function taxCells(path: string, label: string, get: (f: Gstr3bForm) => Tax4) {
    return (['iamt', 'camt', 'samt', 'csamt'] as const).map((k) => (
      <td key={k} className="px-2 py-1 text-right">
        <Num label={`${label} ${k}`} value={get(form)[k]} bad={bad(`${path}.${k}`)} onChange={(v) => change((f) => { get(f)[k] = v; })} />
      </td>
    ));
  }
}

function InterGroup({ kind, form, change, bad }: {
  kind: InterKind; form: Gstr3bForm; change: (fn: (f: Gstr3bForm) => void) => void; bad: (p: string) => boolean;
}) {
  const label = { unreg: 'Supplies made to Unregistered Persons', comp: 'Supplies made to Composition Taxable Persons', uin: 'Supplies made to UIN holders' }[kind];
  const rows = form.inter[kind];
  return (
    <>
      <tr className="border-b border-ink-100 bg-ink-50/40">
        <td className="px-4 py-2 text-xs font-semibold text-ink-700">{label}</td>
        <td /><td />
        <td className="px-2 py-1 text-right">
          <button onClick={() => change((f) => { f.inter[kind].push({ pos: '', txval: 0, iamt: 0 }); })}
            className="inline-flex items-center gap-1 rounded-full border border-ink-300 bg-white px-2.5 py-1 text-[11px] font-semibold text-ink-700 hover:bg-ink-50">
            <Plus className="h-3 w-3" /> Add row
          </button>
        </td>
      </tr>
      {rows.length === 0 && <tr className="border-b border-ink-100"><td className="px-4 py-1.5 text-xs text-ink-400" colSpan={4}>None.</td></tr>}
      {rows.map((r, i) => (
        <tr key={i} className="border-b border-ink-100">
          <td className="px-4 py-1.5">
            <select aria-label={`3.2 ${kind} row ${i + 1} place of supply`} value={r.pos}
              onChange={(e) => change((f) => { f.inter[kind][i].pos = e.target.value; })}
              className={`rounded-lg border px-2 py-1 text-sm ${bad(`inter.${kind}.${i}.pos`) ? 'border-danger-300 bg-danger-50' : 'border-ink-200 bg-white'}`}>
              <option value="">Place of supply…</option>
              {Object.entries(GST_STATES).map(([code, name]) => <option key={code} value={code}>{code} · {name}</option>)}
            </select>
          </td>
          <td className="px-2 py-1 text-right"><Num label={`3.2 ${kind} row ${i + 1} taxable value`} value={r.txval} bad={bad(`inter.${kind}.${i}.txval`)} onChange={(v) => change((f) => { f.inter[kind][i].txval = v; })} /></td>
          <td className="px-2 py-1 text-right"><Num label={`3.2 ${kind} row ${i + 1} IGST`} value={r.iamt} bad={bad(`inter.${kind}.${i}.iamt`)} onChange={(v) => change((f) => { f.inter[kind][i].iamt = v; })} /></td>
          <td className="px-2 py-1 text-right">
            <button aria-label={`Remove 3.2 ${kind} row ${i + 1}`} onClick={() => change((f) => { f.inter[kind].splice(i, 1); })} className="rounded-full p-1.5 text-ink-400 hover:bg-ink-100 hover:text-ink-700"><X className="h-3.5 w-3.5" /></button>
          </td>
        </tr>
      ))}
    </>
  );
}

const HEADS: [string, keyof Heads][] = [['Integrated tax', 'igst'], ['Central tax', 'cgst'], ['State/UT tax', 'sgst'], ['Cess', 'cess']];

function PaymentCard({ pay, blocked }: { pay: ReturnType<typeof payment> | null; blocked: boolean }) {
  if (!pay) {
    return (
      <div className="rounded-2xl border border-danger-200 bg-danger-50/50 px-5 py-4 text-sm text-danger-800">
        6.1 Payment of tax is not shown while {blocked ? 'a field above is invalid' : 'the form cannot be read'} — fix the highlighted figures.
      </div>
    );
  }
  const row = (label: string, v: Heads, opts: { strong?: boolean; muted?: boolean } = {}) => (
    <tr className={`border-b border-ink-100 last:border-0 ${opts.strong ? 'bg-ink-50/70 font-semibold' : ''}`}>
      <td className={`px-4 py-2 ${opts.muted ? 'text-ink-500' : 'text-ink-800'}`}>{label}</td>
      {HEADS.map(([, k]) => <td key={k} className="px-4 py-2 text-right tabular-nums text-ink-800">{inr(v[k])}</td>)}
      <td className="px-4 py-2 text-right tabular-nums text-ink-900">{inr(v.igst + v.cgst + v.sgst + v.cess)}</td>
    </tr>
  );
  return (
    <div className="overflow-hidden rounded-2xl border border-ink-200 bg-white">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-ink-200 bg-ink-50 px-5 py-3">
        <div>
          <p className="text-sm font-semibold text-ink-900">6.1 Payment of tax</p>
          <p className="mt-0.5 max-w-3xl text-xs text-ink-500">
            Credit pays outward tax in the order of section 49(5) and rule 88A: IGST credit to IGST, then CGST, then SGST; CGST or SGST credit to its own head, then IGST;
            never CGST to SGST or the reverse. Reverse-charge tax, interest and late fee are paid in cash only.
          </p>
        </div>
        <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${pay.indicative ? 'bg-warn-100 text-warn-800' : 'bg-ok-100 text-ok-700'}`}>
          {pay.indicative ? 'Indicative — ledgers not read' : 'On the portal’s ledger balances'}
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[42rem] text-sm">
          <thead>
            <tr className="border-b border-ink-100 text-left text-xs uppercase tracking-wide text-ink-400">
              <th className="px-4 py-2.5 font-medium">Description</th>
              {HEADS.map(([l]) => <th key={l} className="px-4 py-2.5 text-right font-medium">{l}</th>)}
              <th className="px-4 py-2.5 text-right font-medium">Total</th>
            </tr>
          </thead>
          <tbody>
            {row('Tax payable — 3.1(a), 3.1(b), 3.1(d)', pay.payable.total)}
            {row('of which reverse charge, 3.1(d) — cash only', pay.payable.reverseCharge, { muted: true })}
            {row(pay.indicative ? 'Credit available — 4C of this return' : 'Credit available — ledger + 4C', pay.credit, { muted: true })}
            {row('Credit applied', pay.creditUsed, { muted: true })}
            {row('Payable after credit (tax)', pay.payableAfterCredit, { strong: true })}
            {row('Interest — 5.1, cash', pay.interest, { muted: true })}
            {row('Late fee — 5.1, cash', pay.lateFee, { muted: true })}
            {row('Cash required', pay.cashRequiredByHead, { strong: true })}
            {pay.shortfall && row('Cash short of what the ledger holds', pay.shortfall)}
          </tbody>
        </table>
      </div>
      {pay.steps.length > 0 && (
        <p className="border-t border-ink-100 px-5 py-2.5 text-xs text-ink-600">
          {pay.steps.map((s) => `${s.from.toUpperCase()} credit → ${s.to.toUpperCase()} ₹${inr(s.amount)}`).join(' · ')}
        </p>
      )}
    </div>
  );
}

function Checks({ issues, ready }: { issues: Issue[]; ready: boolean }) {
  const errs = issues.filter((i) => i.level === 'error');
  const warns = issues.filter((i) => i.level === 'warn');
  return (
    <div className="space-y-2">
      {errs.length > 0 && (
        <div className="rounded-2xl border border-danger-200 bg-danger-50/60 px-5 py-3">
          <p className="flex items-center gap-2 text-sm font-semibold text-danger-800"><AlertTriangle className="h-4 w-4" /> {errs.length} invalid — no upload payload is built until these are fixed</p>
          <ul className="mt-1.5 list-disc space-y-0.5 pl-6 text-xs text-danger-800">{errs.map((i, n) => <li key={n}>{i.message}</li>)}</ul>
        </div>
      )}
      {warns.length > 0 && (
        <div className="rounded-2xl border border-warn-200 bg-warn-50 px-5 py-3">
          <p className="flex items-center gap-2 text-sm font-semibold text-warn-800"><AlertTriangle className="h-4 w-4" /> {warns.length} to look at</p>
          <ul className="mt-1.5 list-disc space-y-0.5 pl-6 text-xs text-warn-800">{warns.map((i, n) => <li key={n}>{i.message}</li>)}</ul>
        </div>
      )}
      {ready && (
        <p className="flex items-center gap-2 rounded-2xl border border-ok-200 bg-ok-50/60 px-5 py-3 text-sm text-ok-700">
          <CheckCircle2 className="h-4 w-4 shrink-0" /> Every field is valid; an upload payload can be built from this form.
        </p>
      )}
    </div>
  );
}

